import { useEffect, useState } from "react";

/**
 * True once the referenced element comes within `rootMargin` of the viewport,
 * and stays true after that.
 *
 * WHY: for deferring heavy things that sit below the fold - the Google Maps
 * API on the home page search, the hero background video - until a visitor
 * scrolls towards them, so they cost nothing for anyone who doesn't.
 *
 * The page scrolls inside #root rather than the window, but that doesn't
 * matter here: IntersectionObserver with no `root` measures against the
 * viewport, clipped by scrolling ancestors, which is exactly "on screen".
 *
 * Browsers without IntersectionObserver get `true` immediately - they load
 * eagerly, as the site did before.
 */
export default function useNearViewport(ref, rootMargin = "300px") {
    const [near, setNear] = useState(
        () => typeof window !== "undefined" && !("IntersectionObserver" in window)
    );

    useEffect(() => {
        if (near || !ref.current) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setNear(true);
                    observer.disconnect();
                }
            },
            { rootMargin }
        );
        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [near, ref, rootMargin]);

    return near;
}
